import { isValid } from 'date-fns'

import { parseUTCDate } from '@/utils/dateUtils'

export const STALE_SCAN_DAYS = 30

export const EDITED_SINCE_SCAN_MESSAGE =
  'This funding request has been edited since the last scan. Re-find matches to get up-to-date results.'

export const MISSING_AMOUNT_SCAN_MESSAGE =
  'Add a funding amount to this request to find matches.'

export const ARCHIVED_SCAN_MESSAGE =
  'This funding request has been archived. Unarchive it to re-find matches.'

export const PAUSED_SCAN_MESSAGE =
  'Your subscription is paused and the app is read-only. Resume it to find matches.'

export const CANCELLED_SCAN_MESSAGE =
  'Your subscription has been cancelled and the app is read-only. Reactivate it to find matches.'

export const READ_ONLY_SCAN_MESSAGE =
  'Your account is read-only, so matches cannot be found right now.'

/**
 * Whether a scan can run for this request. Matching filters opportunities by
 * amount, so the backend refuses a request with neither amount set; the form
 * allows saving one without either.
 */
export function hasScanAmount(fundingNeed: {
  amount?: unknown
  min_amount?: unknown
}): boolean {
  return fundingNeed.amount != null || fundingNeed.min_amount != null
}

/**
 * Why a scan cannot be started, or null when one can. `scope` separates a block
 * on the whole account from one on this request: the account-wide messages
 * repeat what the page banner already says, so a surface without hover shows
 * only the request-level ones.
 *
 * The free-tier scan cap is deliberately absent. Leaving the button live lets
 * the POST return 402, which opens the upgrade dialog — the only place that
 * explains the cap. Disabling the button hid that explanation, and also caught
 * orgs whose usage lookup had merely failed, since unknown usage reads as at
 * cap.
 */
export type ScanBlock = { message: string; scope: 'account' | 'request' } | null

export function getScanBlock({
  canWrite,
  isPaused,
  isCancelled,
  isArchived,
  hasAmount,
}: {
  canWrite: boolean
  isPaused?: boolean
  isCancelled?: boolean
  isArchived?: boolean
  hasAmount: boolean
}): ScanBlock {
  if (!canWrite) {
    const message = isCancelled
      ? CANCELLED_SCAN_MESSAGE
      : isPaused
        ? PAUSED_SCAN_MESSAGE
        : READ_ONLY_SCAN_MESSAGE
    return { message, scope: 'account' }
  }
  if (isArchived) return { message: ARCHIVED_SCAN_MESSAGE, scope: 'request' }
  if (!hasAmount)
    return { message: MISSING_AMOUNT_SCAN_MESSAGE, scope: 'request' }
  return null
}

/**
 * The date staleness is measured from, and whether the most recent scan found
 * nothing. A scan that finds zero candidate opportunities writes no
 * scan_results rows, only funding_needs.last_scanned_at -- and only that kind
 * of scan writes it -- so when last_scanned_at is the later of the two, the
 * most recent scan found nothing. Reading the older scan_results date instead
 * would leave a request that matched nothing stale forever.
 */
export function getLatestScan(
  resultsScanDate: string | Date | null | undefined,
  lastScannedAt: string | Date | null | undefined,
): { scanDate: Date | null; latestScanFoundNothing: boolean } {
  const fromResults = resultsScanDate ? parseUTCDate(resultsScanDate) : null
  const fromZeroResultScan = lastScannedAt ? parseUTCDate(lastScannedAt) : null
  const latestScanFoundNothing = Boolean(
    fromZeroResultScan &&
      isValid(fromZeroResultScan) &&
      !(fromResults && isValid(fromResults) && fromResults >= fromZeroResultScan),
  )
  return {
    scanDate: latestScanFoundNothing ? fromZeroResultScan : fromResults,
    latestScanFoundNothing,
  }
}
